import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { PermissionWithAssignment } from '../../../../../../../../api/types/settings/permissions/permission-group-details.type';
import {
  CheckBoxSeverity,
  ColorfulCheckbox,
} from '../../../../../../../../component/ui/colorful-checkbox/colorful-checkbox';
import { getPermissionActionKeyLabel } from '../../../utils/permission-action-type';
import { permissionResourceLabel } from '../../../utils/permission-labels';
import { updatePermissionGroupPermissionsOperation } from '../../operations/update-permission-group-permissions-operation';

interface PermissionGroupPermissionsTabProps {
  groupId: number;
  permissions: PermissionWithAssignment[];
  onPermissionsChanged: () => void;
}

const clonePermissions = (
  permissions: PermissionWithAssignment[],
): PermissionWithAssignment[] =>
  permissions.map((permission) => ({ ...permission }));

export const PermissionGroupPermissionsTab: React.FC<
  PermissionGroupPermissionsTabProps
> = ({ groupId, permissions, onPermissionsChanged }) => {
  const [editablePermissions, setEditablePermissions] = useState<
    PermissionWithAssignment[]
  >(() => clonePermissions(permissions));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setEditablePermissions(clonePermissions(permissions));
  }, [permissions]);

  const isDirty = useMemo(() => {
    return permissions.some((original) => {
      const edited = editablePermissions.find(
        (item) => item.id === original.id,
      );
      return edited != null && edited.isAssigned !== original.isAssigned;
    });
  }, [permissions, editablePermissions]);

  const toggleAssigned = (permissionId: number) => {
    setEditablePermissions((prev) =>
      prev.map((permission) =>
        permission.id === permissionId
          ? { ...permission, isAssigned: !permission.isAssigned }
          : permission,
      ),
    );
  };

  const handleApprove = async () => {
    const permissionIds = editablePermissions
      .filter((permission) => permission.isAssigned)
      .map((permission) => permission.id);

    await updatePermissionGroupPermissionsOperation(
      groupId,
      permissionIds,
      setLoading,
      onPermissionsChanged,
    );
  };

  return (
    <div style={{ direction: 'rtl', paddingTop: '12px' }}>
      <DataTable
        value={editablePermissions}
        dataKey="id"
        tableStyle={{ direction: 'rtl' }}
        emptyMessage={
          <div style={{ textAlign: 'right' }}>לא נמצאו הרשאות לקבוצה</div>
        }
      >
        <Column
          header="משאב"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(row: PermissionWithAssignment) =>
            permissionResourceLabel(row.resource)
          }
        />
        <Column
          header="פעולה"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(row: PermissionWithAssignment) =>
            getPermissionActionKeyLabel(row.actionKey)
          }
        />
        <Column
          header="תיאור"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(row: PermissionWithAssignment) =>
            row.description?.trim() || '—'
          }
        />
        <Column
          header="מאושר"
          style={{ textAlign: 'center', width: '90px' }}
          headerStyle={{ textAlign: 'center' }}
          body={(row: PermissionWithAssignment) => (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <ColorfulCheckbox
                severity={CheckBoxSeverity.SUCCESS}
                checked={row.isAssigned}
                disabled={loading}
                onChange={() => toggleAssigned(row.id)}
              />
            </div>
          )}
        />
      </DataTable>

      {isDirty && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '16px',
          }}
        >
          <Button
            label="אישור"
            icon="pi pi-check"
            onClick={handleApprove}
            loading={loading}
            disabled={loading}
            style={{ height: '36px' }}
          />
        </div>
      )}
    </div>
  );
};
